export interface IWordList {
  x1: number;
  y1: number;
  x3: number;
  y3: number;
  colorType: "grey" | "black";
  imageData: HTMLImageElement;
  width: number;
  height: number;
  imageUrl: string;
  name: string;
}

interface ICoordinate {
  x: number;
  y: number;
}

const GREY = { r: 167, g: 168, b: 169 }; // "#A7A8A9";
const BLACK = { r: 0, g: 0, b: 0 }; // "#000000";

const BYTES = 4;
const OFFSET = 5; // 범위

export const detectMobileDevice = () => {
  const agent = window.navigator.userAgent;
  const mobileRegex = [
    /Android/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];

  return mobileRegex.some((mobile) => agent.match(mobile));
};

const DPR = detectMobileDevice() ? 5 : 10;

/** 색상 변경되야 하는 영역 찾기 */
const findSegmentArea = ({
  data,
  width,
  height,
}: {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}) => {
  const px = 0;
  const py = 0;
  const result = new Uint8Array(width * height);
  const visited = new Uint8Array(width * height); // 0은 안들린거 1은 들린거
  const stack = [{ y: py, left: px - 1, right: px + 1, dir: 1 }]; // 스캔 데이터

  while (stack.length > 0) {
    const element = stack.shift();
    if (!element) return result;

    for (let x = element.left + 1; x < element.right; x++) {
      const pointY = element.y * width + x;

      if (visited[pointY] === 0) {
        const isColorPicker = findColorBoundary(data, pointY * BYTES);
        result[pointY] = isColorPicker ? 1 : 0;
        visited[pointY] = 1;

        // 현재 좌표의 왼쪽으로 이동
        let left = x - 1;
        while (left > -1) {
          const pointLeft = element.y * width + left;
          const isColorPicker = findColorBoundary(data, pointLeft * BYTES);
          result[pointLeft] = isColorPicker ? 1 : 0;
          visited[pointLeft] = 1;
          left--;
        }

        // 현재 좌표의 오른쪽으로 이동
        let right = x + 1;
        while (right < width) {
          const pointRight = element.y * width + right;
          if (visited[pointRight] === 1) break; // 들렸던 위치면 break

          const isColorPicker = findColorBoundary(data, pointRight * BYTES);
          result[pointRight] = isColorPicker ? 1 : 0;
          visited[pointRight] = 1;
          right++;
        }

        const leftY = element.y - element.dir;
        if (leftY >= 0 && leftY < height) {
          // 왼쪽 방향으로 틀기
          if (left < element.left) {
            stack.push({
              y: leftY,
              left,
              right: element.left,
              dir: -element.dir,
            });
          }

          // 오른쪽 방향으로 틀기
          if (element.right < right) {
            stack.push({
              y: leftY,
              left: element.right,
              right,
              dir: -element.dir,
            });
          }
        }

        const rightY = element.y + element.dir;
        if (rightY >= 0 && rightY < height) {
          // 아래 방향으로 틀기
          if (left < right) {
            stack.push({ y: rightY, left, right, dir: element.dir });
          }
        }
      }
    }
  }

  return result;
};

/** 색상이 칠해져있는 영역 찾기 */
const findColorBoundary = (data: Uint8ClampedArray, index: number) => {
  const red = data[index];
  const green = data[index + 1];
  const blue = data[index + 2];
  const alpha = data[index + 3];

  return !(red === 0 && green === 0 && blue === 0 && alpha === 0);
};

/** 색상 변경 */
export const changeColor = ({
  imageData,
  width,
  height,
  colorType,
}: {
  imageData: HTMLImageElement;
  width: number;
  height: number;
  colorType: "grey" | "black";
}) => {
  // const offScreen = new OffscreenCanvas(width, height);
  const offScreen = document.createElement("canvas");
  offScreen.width = width;
  offScreen.height = height;

  const ctxScreen = offScreen.getContext("2d");
  if (!ctxScreen) return new ImageData(width, height);

  ctxScreen.beginPath();
  ctxScreen.clearRect(0, 0, width, height);
  ctxScreen.drawImage(imageData, 0, 0, width, height);
  ctxScreen.closePath();

  const { data } = ctxScreen.getImageData(0, 0, width, height);

  const segmentArea = findSegmentArea({ data, width, height });
  const fillImgData = new ImageData(new Uint8ClampedArray(data), width, height);

  const color = colorType === "grey" ? GREY : BLACK;

  for (let y = 0; y <= height; y++) {
    for (let x = 0; x <= width; x++) {
      if (segmentArea[y * width + x] !== 0) {
        const curr = (y * width + x) * 4;
        if (fillImgData.data[curr] === 0) {
          fillImgData.data[curr] = color.r;
          fillImgData.data[curr + 1] = color.g;
          fillImgData.data[curr + 2] = color.b;
          fillImgData.data[curr + 3] = 255;
        }
      }
    }
  }

  return fillImgData;
};

/** 현재 마우스가 어떤 도형 위에 있는지 찾기 */
export const findCurrentArea = ({
  wordList,
  offsetX,
  offsetY,
}: {
  wordList: IWordList[];
  offsetX: number;
  offsetY: number;
}) => {
  const result = wordList.find(
    (word) =>
      word.x1 / DPR - OFFSET < offsetX &&
      offsetX < word.x3 / DPR + OFFSET &&
      word.y1 / DPR - OFFSET < offsetY &&
      offsetY < word.y3 / DPR + OFFSET,
  );

  return result;
};

/** 거리 계산하는 함수 */
export function getDistance(p1: ICoordinate, p2: ICoordinate) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;

  return Math.sqrt(dx * dx + dy * dy);
}

/** 각도 계산하는 함수 */
export function getAngle(p1: ICoordinate, p2: ICoordinate) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;

  return Math.atan2(dy, dx);
}
